import { Injectable } from '@angular/core';
import { ContentfulClientApi, createClient, CreateClientParams } from 'contentful';
import { from, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { LogService } from '@rci/log-service';
import { MiscService } from '@rci/misc-service';

export interface ContentfulQuery {
  locale?: string;
  include?: number;
}

type ContentfulConfig = CreateClientParams & {
  appConfigId: string;
};

@Injectable({ providedIn: 'root' })
export class ContentfulServiceConfig {
  contentful: ContentfulConfig = {
    host: '127.0.0.1:3333',
    insecure: true,
    basePath: 'basePath',
    space: 'space',
    accessToken: 'accessToken',
    environment: 'environment',
    appConfigId: 'app-config',
  };
}

@Injectable({
  providedIn: 'root',
})
export class ContentfulService {
  private client: ContentfulClientApi;

  constructor(
    private config: ContentfulServiceConfig,
    private logService: LogService,
    private miscService: MiscService,
  ) {}

  getContentfulEntry(id: string, query?: ContentfulQuery): Observable<any> {
    return from(this.getClient().getEntry(id, query)).pipe(
      tap(content => this.logService.log(`Contentful entry id = ${id}`, query, content)),
      switchMap(result => this.promiseHydration(result, query)),
    );
  }

  async promiseHydration(contentfulObject: any, query?: ContentfulQuery): Promise<any> {
    while (1) {
      const unhydratedNestedObject = this.findUnhydratedNestedObject(contentfulObject);
      if (!unhydratedNestedObject) {
        break;
      }
      unhydratedNestedObject.fields = await this.miscService.toPromise(
        this.getContentfulEntry(unhydratedNestedObject.sys.id, query),
      );
    }
    return contentfulObject;
  }

  private findUnhydratedNestedObject(contentfulObject: any): any {
    return this.miscService.traverseAndFind(contentfulObject, (o: any) => {
      const keys = Object.keys(o);
      return keys.includes('sys') && o.sys.type === 'Link' && o.sys.linkType === 'Entry' && !keys.includes('fields');
    });
  }

  private getClient() {
    if (!this.client) {
      this.client = createClient(this.config.contentful);
    }
    return this.client;
  }
}
